import { Dropdown, Space } from 'antd'
import { toJS } from 'mobx'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useRef, useState } from 'react'
import { IoMdMore } from 'react-icons/io'

import { UIManager } from '@/Core/Managers/UI/UIManager'
import { useProfiler } from '@/Core/Utils/profilerHelpers'

import { useCrudResourceContext } from '@/Core/UI/CrudResource/CrudResourceContextProvider'
import { useDrawerContext } from '@/Core/UI/Drawer/DrawerContextProvider'
import { useFormContext } from '@/Core/UI/Form/Providers/FormContextProvider'
import { useHeaderPageContext } from '@/Core/UI/HeaderPage/HeaderPageContextProvider'
import { useListContext } from '@/Core/UI/List/ListContextProvider'

import { ButtonGroupStore } from './ButtonGroupStore'
import type { ButtonGroupButton, ButtonGroupProps } from './types'

import { Button } from '@/Core/UI/Button'

import './ButtonGroup.css'

/**
 * Фільтрує об'єкт, залишаючи тільки вказані ключі
 */
function filterObject<T extends Record<string, any>>(obj: T, keysArray: string[]): Partial<T> {
    const filteredObj: Partial<T> = {}

    for (const key in obj) {
        if (keysArray.includes(key)) {
            filteredObj[key] = obj[key]
        }
    }

    return filteredObj
}

/**
 * Перевіряє чи два масиви ідентичні
 */
function arraysAreEqual(arr1: string[], arr2: string[]): boolean {
    if (arr1.length !== arr2.length) {
        return false
    }

    return arr1.every((value, index) => value === arr2[index])
}

/**
 * ButtonGroup - адаптивна група кнопок з dropdown меню
 * 
 * Компонент автоматично розподіляє кнопки між основною зоною та dropdown меню
 * в залежності від доступного простору. Підтримує умовне відображення кнопок
 * через depFn та accessFn.
 * 
 * @example
 * ```tsx
 * const buttons = {
 *   Home: { label: 'Home', icon: <HomeIcon />, sort: 1, priority: 'button' },
 *   Settings: { label: 'Settings', icon: <SettingsIcon />, sort: 3, priority: 'dropdown' },
 *   Custom: { children: <MyComponent /> }
 * }
 * 
 * <ButtonGroup buttons={buttons} />
 * ```
 */
export const ButtonGroup: React.FC<ButtonGroupProps> = ({
    buttons,
    content = [],
    dropdownButtonWidth = 40,
    dropdownButtonIcon,
    mobileWidth = 670,
    recalculationThreshold = 50,
}) => {
    // Інтеграція з профайлером в dev режимі
    const { addEvent, setStatus } = useProfiler('ButtonGroup', 'component', '/Core/UI/ButtonGroup/ButtonGroup.tsx', [
        buttons,
    ])

    React.useEffect(() => {
        setStatus('active')
        addEvent('initialized', { buttonsCount: Object.keys(buttons).length })
    }, [])

    const finalDropdownButtonIcon = dropdownButtonIcon || <IoMdMore style={{ fontSize: 22 }} />

    const buttonGroupStore = new ButtonGroupStore()
    const containerRef = useRef<HTMLDivElement>(null)
    const [isMobile, setIsMobile] = useState(window.innerWidth <= mobileWidth)

    const formContext = useFormContext()
    const crudContext = useCrudResourceContext()
    const listContext = useListContext()
    const drawerContext = useDrawerContext()
    const headerPageContext = useHeaderPageContext()

    const [lastCalculatedWidth, setLastCalculatedWidth] = useState(window.innerWidth)

    let buttonsFilteredKeysMemory: string[] = []

    const shouldRecalculate = (currentWidth: number): boolean => {
        return Math.abs(currentWidth - lastCalculatedWidth) >= recalculationThreshold
    }

    /**
     * Внутрішній компонент з MobX observer для реактивності
     */
    const ButtonGroupObserver = observer(() => {
        // Фільтруємо кнопки на основі depFn та accessFn
        const buttonsFilteredKeys = Object.keys(toJS(buttons)).filter((key) => {
            const button = buttons[key]

            if (typeof button?.depFn !== 'function' && typeof button?.accessFn !== 'function') {
                return true
            }

            let result = true

            // Перевірка залежності
            if (typeof button.depFn === 'function') {
                try {
                    const depResult = button.depFn({
                        formContext,
                        crudContext,
                        listContext,
                        drawerContext,
                        headerPageContext,
                        ...(button?.depStore ?? {}),
                    })
                    if (depResult === false) {
                        result = false
                    }
                } catch (e) {
                    console.error('ButtonGroup depFn error:', e)
                    result = false
                }
            }

            // Перевірка доступу
            if (result && typeof button.accessFn === 'function') {
                try {
                    const accessResult = button.accessFn({
                        formContext,
                        crudContext,
                        listContext,
                        drawerContext,
                        headerPageContext,
                        ...(button?.depStore ?? {}),
                    })
                    if (accessResult === false) {
                        result = false
                    }
                } catch (e) {
                    console.error('ButtonGroup accessFn error:', e)
                    result = false
                }
            }

            return result
        })

        const buttonsFiltered = filterObject(buttons, buttonsFilteredKeys)

        useEffect(() => {
            if (!arraysAreEqual(buttonsFilteredKeys, buttonsFilteredKeysMemory)) {
                buttonsFilteredKeysMemory = [...buttonsFilteredKeys]
                buttonGroupStore.setOriginalButtons(buttonsFiltered as Record<string, ButtonGroupButton>)
            }
        }, [buttonsFilteredKeys.join(',')])

        /**
         * Розраховує скільки кнопок можна відобразити в основній зоні
         */
        const calculateVisibleButtons = () => {
            if (!containerRef?.current) return

            const currentWidth = window.innerWidth
            if (!shouldRecalculate(currentWidth)) return

            const primaryButtons = buttonGroupStore.originalButtons.filter((btn) => btn.priority === 'button')
            const dropdownButtons = buttonGroupStore.originalButtons.filter((btn) => btn.priority === 'dropdown')

            const containerWidth = containerRef.current.offsetWidth + 5
            let availableWidth = containerWidth

            let visibleCount = 0

            if (primaryButtons.length > 1 || dropdownButtons.length > 0) {
                availableWidth = containerWidth - dropdownButtonWidth
            }

            const tempContainer = document.createElement('div')
            tempContainer.style.visibility = 'hidden'
            tempContainer.style.position = 'absolute'
            tempContainer.style.display = 'flex'
            document.body.appendChild(tempContainer)

            for (const btn of primaryButtons) {
                const tempButton = document.createElement('button')
                // На мобільному кнопка показує лише іконку (без тексту), тож і
                // ширину міряємо без лейбла; на десктопі — з лейблом.
                tempButton.innerText = !isMobile ? btn.label || '' : ''
                tempButton.style.whiteSpace = 'nowrap'
                tempContainer.appendChild(tempButton)
                const buttonWidth = tempButton.offsetWidth + (!isMobile ? 45 : 30)

                if (availableWidth - buttonWidth >= 0) {
                    availableWidth -= buttonWidth
                    visibleCount++
                } else {
                    break
                }
            }

            if (primaryButtons.length > 0 && visibleCount === 0) {
                visibleCount = 1
            }

            document.body.removeChild(tempContainer)
            buttonGroupStore.adjustButtons(visibleCount)

            setLastCalculatedWidth(currentWidth)
        }

        useEffect(() => {
            const resizeObserver = new ResizeObserver(() => {
                if (shouldRecalculate(window.innerWidth)) {
                    setTimeout(calculateVisibleButtons, 0)
                }
            })

            if (containerRef.current) {
                resizeObserver.observe(containerRef.current)
            }

            const handleResize = () => {
                const currentWidth = window.innerWidth
                if (shouldRecalculate(currentWidth)) {
                    setIsMobile(currentWidth <= mobileWidth)
                    setTimeout(calculateVisibleButtons, 0)
                }
            }

            window.addEventListener('resize', handleResize)

            return () => {
                resizeObserver.disconnect()
                window.removeEventListener('resize', handleResize)
            }
        }, [])

        useEffect(() => {
            setTimeout(calculateVisibleButtons, 0)
        }, [isMobile])

        /**
         * Рендерить окрему кнопку
         */
        const renderButton = (btn: ButtonGroupButton) => {
            const { children, dropdown, dropdownButton, key, label, loading, ...buttonProps } = btn

            // Обробляємо loading: якщо це функція або геттер, викликаємо
            let loadingValue: boolean | { delay?: number; icon?: React.ReactNode } | undefined = loading
            if (typeof loading === 'function') {
                loadingValue = (loading as any)()
            }

            if (children) {
                return children
            }

            // Адаптивність: на мобільному показуємо лише іконку (компактно),
            // на десктопі — іконку + текст.
            //
            // ВАЖЛИВО: на мобільному НЕ загортаємо у antd <Tooltip>. Кастомний
            // Button (Core/UI/Button) не є forwardRef-компонентом, тож Tooltip
            // клонував на нього свій ref + обробники тригера, і на тач-пристроях
            // тап спрацьовував на тултип, а власний onClick кнопки не виконувався
            // (модалка не відкривалась, лише блимав тултип). Підказку даємо через
            // нативний DOM-атрибут title — він не перехоплює клік/тап.
            const buttonRender = (
                <Button
                    key={key ?? Math.random()}
                    className="button-group-item"
                    size="middle"
                    {...(isMobile && typeof label === 'string' ? { title: label } : {})}
                    {...(loadingValue !== undefined ? { loading: loadingValue } : {})}
                    {...buttonProps}
                >
                    {!isMobile ? label : null}
                </Button>
            )

            if (typeof dropdown === 'object') {
                return (
                    <Dropdown trigger={['click']} {...dropdown}>
                        {buttonRender}
                    </Dropdown>
                )
            }

            if (typeof dropdownButton === 'object') {
                // Без Tooltip-обгортки (та сама причина, що й вище) — інакше на
                // мобільному тап не доходив до Dropdown.Button. На мобільному —
                // лише іконка, текст-підказка через нативний title.
                return (
                    <Dropdown.Button
                        className="button-group-item"
                        size="middle"
                        {...(isMobile && typeof label === 'string' ? { title: label } : {})}
                        {...(buttonProps as any)}
                        {...(dropdownButton as any)}
                    >
                        {!isMobile ? label : null}
                    </Dropdown.Button>
                )
            }

            return buttonRender
        }

        const dropdownItems = toJS(buttonGroupStore.dropdownButtons)

        return (
            <>
                <div
                    ref={containerRef}
                    style={{ width: '100%', display: 'flex', justifyContent: 'end' }}
                    className="wl-button-group"
                >
                    <Space>
                        {toJS(buttonGroupStore.buttons).map(renderButton)}
                        {dropdownItems.length > 0 && (
                            <Dropdown
                                menu={{ items: dropdownItems as any }}
                                placement="bottomRight"
                                key="group-button-dropdown"
                            >
                                <Button icon={finalDropdownButtonIcon} className="button-group-item" size="middle" />
                            </Dropdown>
                        )}
                    </Space>
                </div>
                {content}
            </>
        )
    })

    const uiManager = new UIManager({
        template: ButtonGroupObserver,
        isObservable: true,
    })

    return uiManager.render()
}

export default ButtonGroup
